import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UIButton } from 'src/components/ui/Button'
import { cx } from 'src/utils/cx'

interface StepCardProps {
  number: number
  title: string
  description: string
}

const StepCard: FC<StepCardProps> = ({ number, title, description }) => {
  return (
    <div className="relative isolate">
      <div className="absolute -left-2 -top-4 text-8xl font-bold text-primary/5 select-none">
        {number}
      </div>
      <div className="relative z-10">
        <h3 className="text-lg font-bold mb-2 text-primary">{title}</h3>
        <p className="text-primary-light text-sm leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  )
}

interface BenefitCardProps {
  icon: string
  title: string
  description: string
}

const BenefitCard: FC<BenefitCardProps> = ({ icon, title, description }) => {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white text-3xl">
        {icon}
      </div>
      <h3 className="text-lg font-bold mb-3 text-primary">{title}</h3>
      <p className="text-primary-light text-sm leading-relaxed">
        {description}
      </p>
    </div>
  )
}

interface FAQItemProps {
  question: string
  answer: string
}

const FAQItem: FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="border-b border-neutral-200 dark:border-neutral-700">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center gap-4 text-left"
      >
        <span
          className={cx(
            'text-xl text-primary transition-transform duration-200',
            isOpen && 'rotate-45',
          )}
        >
          +
        </span>
        <span className="text-base font-medium text-primary">{question}</span>
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <p className="pl-9 pb-4 text-primary-light text-sm leading-relaxed whitespace-pre-line">
            {answer}
          </p>
        </div>
      </div>
    </div>
  )
}

interface FeatureSectionProps {
  image: string
  imageAlt: string
  title: string
  description: string
  ctaText: string
  onCtaClick: () => void
  imagePosition?: 'left' | 'right'
  className?: string
}

const FeatureSection: FC<FeatureSectionProps> = ({
  image,
  imageAlt,
  title,
  description,
  ctaText,
  onCtaClick,
  imagePosition = 'left',
  className,
}) => {
  const isImageRight = imagePosition === 'right'

  return (
    <div
      className={cx(
        'grid lg:grid-cols-2 gap-8 lg:gap-16 items-center',
        className,
      )}
    >
      <div className={cx('relative', isImageRight && 'order-1 lg:order-2')}>
        <img
          src={image}
          alt={imageAlt}
          className="w-full max-w-2xl mx-auto drop-shadow-xl rounded-lg"
        />
      </div>

      <div
        className={cx(
          'flex flex-col items-start text-left',
          isImageRight && 'order-2 lg:order-1',
        )}
      >
        <h3 className="text-2xl md:text-3xl font-bold mb-6 text-primary">
          {title}
        </h3>
        <p className="text-primary-light text-base leading-relaxed mb-8 whitespace-pre-line">
          {description}
        </p>
        <UIButton
          color="theme"
          variant="filled"
          onPress={onCtaClick}
          className={cx(
            'w-auto h-12 px-7 text-white disabled:bg-opacity-40 rounded-2xl text-base font-semibold',
            'bg-gradient-to-r from-violet-600 to-purple-600',
            'hover:from-violet-500 hover:to-purple-500 hover:shadow-lg hover:shadow-violet-500/25',
            'dark:from-violet-600 dark:to-purple-600 dark:hover:from-violet-500 dark:hover:to-purple-500',
            'active:scale-[0.98]',
            'transition-all duration-300 ease-in-out',
          )}
        >
          {ctaText}
        </UIButton>
      </div>
    </div>
  )
}

// Feature configuration for section 1 (feature 1-4)
const SECTION1_FEATURES = [
  'feature',
  'feature2',
  'feature3',
  'feature4',
] as const

// Feature configuration for section 2 (feature 5-8)
const SECTION2_FEATURES = [
  'feature5',
  'feature6',
  'feature7',
  'feature8',
] as const

// Steps configuration
const STEPS = ['step1', 'step2', 'step3', 'step4', 'step5'] as const

// Benefits configuration with icons
const BENEFITS = [
  { key: 'benefit1', icon: '⚡' },
  { key: 'benefit2', icon: '🎯' },
  { key: 'benefit3', icon: '📦' },
  { key: 'benefit4', icon: '💎' },
] as const

// FAQ configuration
const FAQ_ITEMS = [
  'q1',
  'q2',
  'q3',
  'q4',
  'q5',
  'q6',
  'q7',
  'q8',
  'q9',
] as const

const TrainLoraIntro: FC = () => {
  const { t } = useTranslation(['trainlora'])

  const scrollToTrainForm = () => {
    const el = document.getElementById('train-lora-form-top')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const renderFeatureSection = (
    featureKey: string,
    index: number,
    isFirstInSection: boolean,
  ) => (
    <FeatureSection
      key={featureKey}
      image={t(`trainlora:intro.${featureKey}.image`)}
      imageAlt={t(`trainlora:intro.${featureKey}.title`)}
      title={t(`trainlora:intro.${featureKey}.title`)}
      description={t(`trainlora:intro.${featureKey}.description`)}
      ctaText={t('trainlora:intro.cta.button')}
      onCtaClick={scrollToTrainForm}
      imagePosition={index % 2 === 0 ? 'left' : 'right'}
      className={isFirstInSection ? undefined : 'mt-16'}
    />
  )

  return (
    <div className="min-h-screen bg-background text-primary">
      <section className="max-w-screen-mlg mx-auto px-6 pt-16 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight text-primary">
          {t('trainlora:intro.hero.title')}
        </h2>
        <p className="text-primary-light text-base mx-auto leading-relaxed whitespace-pre-line">
          {t('trainlora:intro.hero.subtitle')}
        </p>
      </section>

      <section className="max-w-screen-mlg mx-auto px-6 py-6 md:py-12">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-primary text-center">
          {t('trainlora:intro.section.title')}
        </h2>
        <p className="text-primary-light text-base mb-8 whitespace-pre-line text-center">
          {t('trainlora:intro.section.subtitle')}
        </p>
        {SECTION1_FEATURES.map((featureKey, index) =>
          renderFeatureSection(featureKey, index, index === 0),
        )}
      </section>

      <section className="max-w-screen-mlg mx-auto px-6 py-6 md:py-12">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-primary text-center">
          {t('trainlora:intro.section2.title')}
        </h2>
        <p className="text-primary-light text-base mb-8 whitespace-pre-line text-center">
          {t('trainlora:intro.section2.subtitle')}
        </p>
        {SECTION2_FEATURES.map((featureKey, index) =>
          renderFeatureSection(featureKey, index, index === 0),
        )}
      </section>

      <section className="bg-background text-primary pt-16">
        <div className="max-w-screen-mlg mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">
            {t('trainlora:intro.section3.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {STEPS.map((stepKey, index) => (
              <StepCard
                key={stepKey}
                number={index + 1}
                title={t(`trainlora:intro.steps.${stepKey}.title`)}
                description={t(`trainlora:intro.steps.${stepKey}.description`)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-screen-mlg mx-auto px-6 pt-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center text-primary">
          {t('trainlora:intro.section4.title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {BENEFITS.map(({ key, icon }) => (
            <BenefitCard
              key={key}
              icon={icon}
              title={t(`trainlora:intro.benefits.${key}.title`)}
              description={t(`trainlora:intro.benefits.${key}.description`)}
            />
          ))}
        </div>
      </section>

      <section className="max-w-screen-mlg mx-auto px-6 py-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center text-primary">
          {t('trainlora:intro.faq.title')}
        </h2>
        <div className="max-w-3xl mx-auto">
          {FAQ_ITEMS.map(faqKey => (
            <FAQItem
              key={faqKey}
              question={t(`trainlora:intro.faq.${faqKey}.question`)}
              answer={t(`trainlora:intro.faq.${faqKey}.answer`)}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

export default TrainLoraIntro
